import {
  GameSnakeData,
  GameSnakeApperanceType,
  GameSnakeGeneralData,
  GameSnakeLayoutData,
  GameSnakeAppleAppearanceData,
  GameSnakeSnakeAppearanceData,
  GameSnakeCustomLabelsData,
  GameSnakeAdvancedData
} from '@/components/games/snake/Data';
import useDevice, { getDeviceData } from '@/hooks/useDevice';
import { GameIndicatorIcon, GameIndicatorSettingsModel } from '@/components/indicators/Model';
import store from '@/store';
import { GameIndicator, GameModel, GameTimeChallenge, HasGameTimeChallenge } from '@/models/GameModel';
import { transformMeasurementToNumber } from '@/utilities/Utilities';
import { CampaignLayoutState } from '@/models/CampaignModel';
import { CSSProperties } from 'vue';

export interface GameSnakeLayoutState {
  size: number;
  width: number;
  widthSizeIsPercentage: boolean;
  backgroundImage?: string;
  backgroundColor: string;
  backgroundType?: string;
  backgroundPosition?: string;
  borderWidth: number;
  borderColor: string;
  grid: boolean;
  gridColor?: string;
  gridWidth?: number;
}

export interface GameSnakeSnakeAppearanceState {
  snakeAppearance: GameSnakeApperanceType;
  snakeColor: string;
  snakeBorderRadius: number;
  snakeImage?: string;
}

export interface GameSnakeAppleAppearanceState {
  appleAppearance: GameSnakeApperanceType;
  appleColor: string;
  appleBorderRadius: number;
  appleImage?: string;
}

export interface GameSnakeCustomLabelsState {
  instructions?: string;
}

export interface GameSnakeGeneralState {
  speedUpTime?: boolean;
  layout?: GameSnakeLayoutState;
  snake?: GameSnakeSnakeAppearanceState;
  apple?: GameSnakeAppleAppearanceState;
  customLabels?: GameSnakeCustomLabelsState;
  enableBestTime?: boolean;
  enableTimeRanking?: boolean;
  instructions?: string;
}

export interface GameSnakeAdvancedSoundState {
  enabled: boolean;
  src?: string;
}
export interface GameSnakeAdvancedState {
  sound: GameSnakeAdvancedSoundState;
}

interface PointsToWin {
  enabled: boolean;
  value: number;
}

export interface GameSnakeState {
  isMobile: boolean;
  snakeSize: number;
  general: GameSnakeGeneralState;
  pointsToWin: PointsToWin;
  elementStyling: GameSnakeElementStyling;
  advanced: GameSnakeAdvancedState;
  indicators?: GameIndicatorSettingsModel;
}

export interface GameSnakeElementStyling {
  snakeTileColor?: CSSProperties;
  snake?: CSSProperties;
  board?: CSSProperties;
  tile?: CSSProperties;
  snakeTile?: string;
  appleTile?: string;
  column?: CSSProperties;
  cssStyle?: string;
}

export enum KeyCode {
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight'
}

export enum SnakeBackgroundType {
  TILE = 'tile',
  ACTUAL_SIZE = 'actual_size',
  STRETCH = 'stretch',
  CONTAIN = 'contain',
  COVER = 'cover'
}

export class GameSnakeModel extends GameModel<GameSnakeData, GameSnakeState> implements HasGameTimeChallenge {
  parseGame(data: GameSnakeData) {
    const state = this.state;
    const campaignState = store.getters['campaign/state'];

    let isMobile = useDevice().isMobile;

    if (typeof isMobile === 'string') {
      isMobile = isMobile === '1';
    }

    state.isMobile = isMobile;
    state.general = GameSnakeModel.constructGeneralState(data.general);
    state.general.layout = GameSnakeModel.parseLayoutDeviceData(data.general);
    state.general.snake = GameSnakeModel.parseSnakeDeviceData(data.general);
    state.general.apple = GameSnakeModel.parseAppleDeviceData(data.general);
    state.general.customLabels = GameSnakeModel.parseCustomLabelsDeviceData(data.general);

    state.advanced = state.advanced ?? {};
    state.advanced.sound = GameSnakeModel.constructAdvancedSoundState(data?.advanced);

    state.elementStyling = state.elementStyling ?? {};

    state.elementStyling.snakeTileColor = {
      backgroundColor: campaignState.layout?.baseColor ? campaignState.layout.baseColor : ''
    };

    state.pointsToWin = {
      enabled: !!(data.general.points_to_win && Number(data.general.points_to_win) > 0),
      value: Number(data.general.points_to_win)
    };

    if (state.general.layout) {
      state.elementStyling.column = GameSnakeModel.constructColumnStyle(state.general.layout);
      state.elementStyling.tile = GameSnakeModel.constructTileStyle(state.general.layout);
      state.elementStyling.board = GameSnakeModel.constructBoardStyle(state.general.layout);
      state.elementStyling.snake = GameSnakeModel.constructSnakeStyle(state.general.layout);
      state.elementStyling.cssStyle = GameSnakeModel.constructCSSStyle(state.general.layout);

      if (state.general.snake) {
        state.elementStyling.snakeTile = GameSnakeModel.constructSnakeTileSnakeStyle(state.general.snake);
      }
      if (state.general.apple) {
        state.elementStyling.appleTile = GameSnakeModel.constructSnakeTileAppleStyle(state.general.apple);
      }
    }
  }

  private static constructAdvancedSoundState(data: GameSnakeAdvancedData | undefined): GameSnakeAdvancedSoundState {
    return {
      enabled: !!data?.sound,
      ...(data?.sound && { src: data.sound })
    };
  }

  private static constructColumnStyle(state: GameSnakeLayoutState): CSSProperties {
    return {
      width: `${100 / state.size}%`
    };
  }

  private static constructTileStyle(state: GameSnakeLayoutState): CSSProperties {
    return {
      ...(state.backgroundImage &&
        state.backgroundType === SnakeBackgroundType.TILE && {
          backgroundImage: `url("${state.backgroundImage}")`,
          backgroundSize: 'cover'
        }),
      ...(state.grid && { marginBottom: `1px` }),
      ...(state.backgroundColor && { backgroundColor: state.backgroundColor }),
      ...(state.grid &&
        state.gridWidth && {
          marginBottom: state.grid ? `${state.gridWidth}px` : '0px',
          borderWidth: state.grid ? `${state.gridWidth}px ${state.gridWidth}px 0 0` : '0px'
        }),
      ...(state.gridColor && { borderColor: state.gridColor })
    };
  }

  private static constructBoardStyle(state: GameSnakeLayoutState): CSSProperties {
    return {
      ...(state.backgroundImage &&
        state.backgroundType !== SnakeBackgroundType.TILE && {
          backgroundImage: `url("${state.backgroundImage}")`,
          ...(state.backgroundType === SnakeBackgroundType.ACTUAL_SIZE && {
            backgroundRepeat: 'no-repeat'
          }),
          ...(state.backgroundType === SnakeBackgroundType.STRETCH && {
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%'
          }),
          ...(state.backgroundType === SnakeBackgroundType.CONTAIN && {
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain'
          }),
          ...(state.backgroundType === SnakeBackgroundType.COVER && {
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
          }),
          ...(state.backgroundPosition && {
            backgroundPosition: state.backgroundPosition.replace('_', ' ')
          })
        }),
      ...(state.grid &&
        state.gridWidth && {
          borderWidth: `${state.gridWidth}px`,
          borderStyle: 'solid',
          gap: `${state.gridWidth}px`
        }),
      ...(state.gridColor && {
        borderColor: `${state.gridColor}`,
        ...(!state.backgroundImage && { backgroundColor: `${state.gridColor}` })
      }),
      ...(state.borderWidth &&
        state.borderColor && {
          outline: `${state.borderWidth}px solid ${state.borderColor}`
        })
    };
  }

  private static constructSnakeStyle(state: GameSnakeLayoutState): CSSProperties {
    let sizeMeasurement = 'px';
    if (state.widthSizeIsPercentage) {
      sizeMeasurement = '%';
    }

    return {
      width: `${state.width}${sizeMeasurement}`
    };
  }

  private static constructSnakeTileSnakeStyle(state: GameSnakeSnakeAppearanceState): string {
    let style = '';

    if (state.snakeImage) {
      style += `.snake__tile--snake:before {
       background-image: url("${state.snakeImage}");
       background-color: transparent;
      }`;
    } else {
      style += `
      .snake__tile--snake:before {
        background-color: ${state.snakeColor};
        border-radius: ${state.snakeBorderRadius}px
      }
    `;
    }

    return style;
  }

  private static constructSnakeTileAppleStyle(state: GameSnakeAppleAppearanceState): string {
    let style = '';

    if (state.appleImage) {
      style += `.snake__tile--apple:before {
       background-image: url("${state.appleImage}");
       background-color: transparent;
     }`;
    } else {
      style += `
      .snake__tile--apple:before {
        background-color: ${state.appleColor};
        border-radius: ${state.appleBorderRadius}px
      }
    `;
    }

    return style;
  }

  private static constructCSSStyle(state: GameSnakeLayoutState): string {
    let layout: CampaignLayoutState | undefined;
    if (store.getters['campaign/state']) {
      layout = store.getters['campaign/state'].layout;
    }
    let style = '';

    if (layout) {
      style += `.site--mobile .theme-base-color-type-bg-color { background-color: ${layout.baseColor} !important;}
      .site--mobile .theme-button-text-color-type-border-color-before:before {border-color: ${layout.backgroundColor}; }`;
    }

    if (state.grid) {
      style += `
        .site .snake__board {
          gap: 1px;
          background-color: #ffffff;
          border-width: 1px;
          border-style: solid;
        }
      `;

      if (state.backgroundImage) {
        const gridWith = state.gridWidth || 1;
        const gridColor = state.gridColor || `#ffffff`;
        const bgColor = state.backgroundColor || `#000000`;

        style += `.site .snake .snake__board {
          background-color: ${bgColor};
        }
        .snake__tile {
          background-color: transparent !important;
          border: 0 none !important;
          outline: ${gridWith}px solid ${gridColor};
        }`;
      }
    }

    return style;
  }

  private static parseCustomLabelsDeviceData(data: GameSnakeGeneralData): GameSnakeCustomLabelsState | undefined {
    if (data) {
      const useData = getDeviceData<GameSnakeCustomLabelsData>(data.customlabels);

      if (!useData) {
        return undefined;
      }
      return GameSnakeModel.constructCustomLabelsState(useData);
    }
  }

  private static constructCustomLabelsState(data: GameSnakeCustomLabelsData): GameSnakeCustomLabelsState {
    return {
      ...(data?.instructions && { instructions: data.instructions })
    };
  }

  private static parseAppleDeviceData(data: GameSnakeGeneralData): GameSnakeAppleAppearanceState | undefined {
    if (data.apple) {
      const useData = getDeviceData(data.apple) as GameSnakeAppleAppearanceData | undefined;

      if (!useData) {
        return undefined;
      }

      return GameSnakeModel.constructAppleAppearanceState(useData);
    }
  }

  private static constructAppleAppearanceState(data: GameSnakeAppleAppearanceData): GameSnakeAppleAppearanceState {
    return {
      appleAppearance: data.apple_appearance,
      ...(data?.apple_image && { appleImage: data.apple_image }),
      appleColor: data.apple_color,
      appleBorderRadius: Number(data.apple_border_radius)
    };
  }

  private static parseSnakeDeviceData(data: GameSnakeGeneralData): GameSnakeSnakeAppearanceState | undefined {
    if (data) {
      const useData = getDeviceData(data.snake) as GameSnakeSnakeAppearanceData | undefined;

      if (!useData) {
        return undefined;
      }
      return GameSnakeModel.constructSnakeAppearanceState(useData);
    }
  }

  private static constructSnakeAppearanceState(data: GameSnakeSnakeAppearanceData): GameSnakeSnakeAppearanceState {
    return {
      snakeAppearance: data.snake_appearance,
      ...(data?.snake_image && { snakeImage: data.snake_image }),
      snakeColor: data.snake_color,
      snakeBorderRadius: Number(data.snake_border_radius)
    };
  }

  private static parseLayoutDeviceData(data: GameSnakeGeneralData): GameSnakeLayoutState | undefined {
    if (data) {
      const useData = getDeviceData(data.layout) as GameSnakeLayoutData | undefined;

      if (!useData) {
        return undefined;
      }
      return GameSnakeModel.constructLayoutSnakeState(useData);
    }
  }

  private static constructLayoutSnakeState(data: GameSnakeLayoutData): GameSnakeLayoutState {
    const widthSize = transformMeasurementToNumber(data.width);

    return {
      size: data?.size ? Number(data?.size) : 20,
      width: widthSize,
      widthSizeIsPercentage: !!data.width?.includes('%'),
      ...(data?.background_type && {
        backgroundType: data?.background_type
      }),
      ...(data?.background_image !== '' && {
        backgroundImage: data.background_image
      }),
      ...(data?.background_position !== '' && {
        backgroundPosition: data.background_position
      }),
      backgroundColor: data.background_color,
      borderWidth: Number(data.border_width),
      borderColor: data.border_color,
      grid: data.grid === '1',
      ...(data?.grid_color && { gridColor: data.grid_color }),
      ...(data?.grid_width && { gridWidth: Number(data.grid_width) })
    };
  }

  private static constructGeneralState(data: GameSnakeGeneralData): GameSnakeGeneralState {
    const device = useDevice();
    return {
      ...(device.isDesktop && {
        instructions: data?.desktop_instructions
      }),

      ...(device.isMobile && {
        instructions: data?.mobile_instructions
      }),
      ...(data?.speed_up_time && {
        speedUpTime: data.speed_up_time === '1'
      }),
      ...(data?.enable_best_time && {
        enableBestTime: data.enable_best_time === '1'
      }),
      ...(data?.enable_time_ranking && {
        enableTimeRanking: data.enable_time_ranking === '1'
      })
    };
  }

  public getIndicators(): GameIndicator[] {
    return [
      {
        indicatorKey: 'time',
        metricKey: {
          time_left: 'timeleft',
          time_used: 'timeused'
        },
        icon: GameIndicatorIcon.TIME,
        value: {
          time_used: 0,
          time_left: this.state.timeChallenge?.limit ?? 0
        }
      },
      {
        indicatorKey: 'score',
        metricKey: {
          score_used: 'points'
        },
        icon: GameIndicatorIcon.SCORE,
        value: {
          score_used: 0
        }
      }
    ];
  }

  parseTimeChallenge(): GameTimeChallenge | undefined {
    const data = this.getData();

    return {
      enabled: data.general.time === 1,
      ...(data.general.time_limit && { limit: Number(data.general.time_limit) })
    };
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen';
  }
}
